/**
 * Shared fictional fixtures for Task 5 recommendation tests.
 * Issuers: "Test Issuer A/B/C" only. Display suffix 1111.
 */

import { asCents, type Cents } from '../money';
import { businessDate, type BusinessDate } from '../time';
import type { CardEligibilityContext } from '../eligibility';
import type { CardState } from '../state';
import type { GraceResult } from '../grace';
import type {
  BenefitInput,
  CapPeriodInput,
  MerchantInput,
  OfferInput,
  RankCardInput,
  RankCardsInput,
  RewardRuleInput,
  SettingsInput,
  SpendCategory,
} from './types';

export const AS_OF: BusinessDate = businessDate('2025-06-15');

export const STORE = 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa';
export const DINING = 'bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb';
export const GROCERY = 'cccccccc-cccc-4ccc-8ccc-cccccccccccc';
export const GAS = 'dddddddd-dddd-4ddd-8ddd-dddddddddddd';
export const TRAVEL = 'eeeeeeee-eeee-4eee-8eee-eeeeeeeeeeee';
export const FX = 'ffffffff-ffff-4fff-8fff-ffffffffffff';
export const PROTECT = '11111111-1111-4111-8111-111111111111';
export const POINTS = '22222222-2222-4222-8222-222222222222';
export const UNVERIFIED = '33333333-3333-4333-8333-333333333333';

export const defaultSettings = (over: Partial<SettingsInput> = {}): SettingsInput => ({
  utilization_warning_bps: 3000,
  utilization_hard_bps: 8000,
  require_active_grace_for_spending: true,
  allow_interest_bearing_recommendations: false,
  treat_likely_active_as_eligible: false,
  min_confidence_to_recommend: 'user_reported',
  assumed_worst_apr_bps: 2990,
  assume_pay_before_close: false,
  financial_staleness_days: 7,
  ...over,
});

function emptyState(cardId: string, over: Partial<CardState['observed']> = {}): CardState {
  return {
    card_id: cardId,
    observed: {
      current_balance_cents: asCents(0n),
      current_balance_cents_observed_at: '2025-06-10T12:00:00Z',
      current_balance_cents_source: 'logged_in_account',
      available_credit_cents: asCents(500_000n),
      available_credit_cents_observed_at: '2025-06-10T12:00:00Z',
      available_credit_cents_source: 'logged_in_account',
      credit_limit_cents: asCents(500_000n),
      credit_limit_cents_observed_at: '2025-06-10T12:00:00Z',
      credit_limit_cents_source: 'logged_in_account',
      statement_balance_cents: asCents(0n),
      statement_balance_cents_observed_at: '2025-06-01T12:00:00Z',
      payment_due_date: '2025-06-25',
      payment_due_date_observed_at: '2025-06-01T12:00:00Z',
      statement_closing_date: '2025-06-01',
      statement_closing_date_observed_at: '2025-06-01T12:00:00Z',
      next_closing_date: '2025-07-01',
      next_closing_date_observed_at: '2025-06-01T12:00:00Z',
      ...over,
    },
    estimated_posted_balance: asCents(0n),
    estimated_pending_balance: asCents(0n),
    estimated_available_credit: asCents(500_000n),
    unreconciled_delta: asCents(0n),
    state_age_days: 5,
    same_day_ambiguous: false,
  };
}

export function eligibility(
  cardId: string,
  grace: Partial<GraceResult> = {},
  stateOver: {
    available?: Cents;
    limit?: Cents;
    posted?: Cents;
    ageDays?: number | null;
    observedAt?: string;
  } = {},
): CardEligibilityContext {
  const available = stateOver.available ?? asCents(500_000n);
  const limit = stateOver.limit ?? asCents(500_000n);
  const posted = stateOver.posted ?? asCents(0n);
  const observedAt = stateOver.observedAt ?? '2025-06-10T12:00:00Z';
  const state = emptyState(cardId, {
    available_credit_cents: available,
    available_credit_cents_observed_at: observedAt,
    credit_limit_cents: limit,
    credit_limit_cents_observed_at: observedAt,
    current_balance_cents: posted,
    current_balance_cents_observed_at: observedAt,
  });
  state.estimated_available_credit = available;
  state.estimated_posted_balance = posted;
  state.state_age_days = stateOver.ageDays === undefined ? 5 : stateOver.ageDays;

  return {
    state,
    grace: {
      status: 'active',
      reason: 'Paid in full by due date on prior statement.',
      derivedFrom: 'derivation',
      paid_in_full_due_date: '2025-05-25',
      ...grace,
    },
    capacity: {
      estimated_available_credit: available,
      available_credit_observed_at: observedAt,
      state_age_days: state.state_age_days,
    },
  };
}

export function card(
  id: string,
  over: Partial<RankCardInput> & { eligibility?: CardEligibilityContext } = {},
): RankCardInput {
  const { eligibility: elig, ...rest } = over;
  return {
    card_id: id,
    status: 'open',
    spend_policy: 'eligible',
    network: 'visa',
    closed_loop_merchant: null,
    foreign_txn_fee_bps: 0,
    purchase_apr_bps: 1990,
    disposition: 'keep',
    owner: 'personal',
    annual_fee_cents: asCents(0n),
    unresolved_past_due: false,
    foreign_txn_fee_verified_at: '2025-01-15',
    eligibility: elig ?? eligibility(id),
    ...rest,
  };
}

export function baseRule(
  id: string,
  cardId: string,
  rateBps: number,
  over: Partial<RewardRuleInput> = {},
): RewardRuleInput {
  return {
    id,
    card_id: cardId,
    scope: 'account_specific',
    reward_rate_bps: rateBps,
    milli_points_per_dollar: null,
    milli_cents_per_point: null,
    category: null,
    merchant_match: 'any',
    merchant_names: null,
    channel: 'any',
    geography: 'any',
    requires_activation: false,
    activated: null,
    requires_membership: false,
    membership_active: null,
    spend_cap_cents: null,
    post_cap_rate_bps: null,
    valid_from: null,
    valid_until: null,
    mutually_exclusive_with: [],
    stacking: 'stacks_with_offers',
    priority: 0,
    confidence: 'account_agreement',
    last_verified_at: '2025-01-01',
    ...over,
  };
}

export function bonusRule(
  id: string,
  cardId: string,
  category: SpendCategory,
  rateBps: number,
  over: Partial<RewardRuleInput> = {},
): RewardRuleInput {
  return baseRule(id, cardId, rateBps, {
    category,
    merchant_match: 'any',
    priority: 10,
    ...over,
  });
}

export function merchant(
  over: Partial<MerchantInput> & Pick<MerchantInput, 'id' | 'normalized_name'>,
): MerchantInput {
  return {
    default_category: null,
    online_category: null,
    in_store_category: null,
    is_intermediary: false,
    category_confidence: 'issuer_public_page',
    ...over,
  };
}

export function query(
  over: Partial<RankCardsInput['query']> &
    Pick<RankCardsInput['query'], 'merchant_input' | 'amount_cents'>,
): RankCardsInput['query'] {
  return {
    merchant_id: null,
    category: null,
    channel: 'in_store',
    geography: 'domestic',
    recurring: false,
    owner: 'personal',
    financing_requested: false,
    return_risk: false,
    mode: 'maximize_rewards',
    pay_before_statement_close: null,
    allow_interest_bearing: false,
    ...over,
  };
}

export function input(over: Partial<RankCardsInput>): RankCardsInput {
  return {
    query: query({ merchant_input: 'Test Merchant', amount_cents: asCents(10_000n) }),
    merchant: null,
    cards: [],
    rewardRules: [],
    offers: [],
    benefits: [],
    capPeriods: [],
    categoryObservations: [],
    settings: defaultSettings(),
    asOf: AS_OF,
    ...over,
  };
}

export function benefit(
  cardId: string,
  kind: BenefitInput['kind'],
  over: Partial<BenefitInput> = {},
): BenefitInput {
  return {
    card_id: cardId,
    kind,
    confirmed_absent: false,
    position: 'primary',
    ...over,
  };
}

export function offer(id: string, cardId: string, over: Partial<OfferInput> = {}): OfferInput {
  return {
    id,
    card_id: cardId,
    merchant_id: null,
    kind: 'percent_back',
    value_bps: 1000,
    value_cents: null,
    minimum_spend_cents: null,
    maximum_reward_cents: null,
    activation_required: true,
    activated: true,
    valid_from: '2025-01-01',
    valid_until: '2025-12-31',
    channel: 'any',
    confidence: 'logged_in_account',
    last_verified_at: '2025-06-01',
    ...over,
  };
}

export function cap(ruleId: string, remaining: Cents): CapPeriodInput {
  return { reward_rule_id: ruleId, remaining_cap_cents: remaining };
}

/** Stable JSON for bigint-containing traces. */
export function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, current) => {
    if (typeof current === 'bigint') return current.toString();
    return current as string | number | boolean | null | object | undefined;
  });
}
